import { useEffect, useMemo, useState } from 'react'
import { X, Pin, PinOff } from 'lucide-react'
import type { Requirement, RequirementNode, RequirementSection } from '@ispot/shared'
import {
  REQUIREMENT_DOC_SECTIONS,
  REQUIREMENT_DOC_SECTION_LABELS,
  getRequirementSection,
} from '@ispot/shared'

interface Props {
  tree: RequirementNode[]
  requirements: Requirement[]
  pinned: boolean
  onTogglePin: () => void
  onClose: () => void
}

interface SectionGroup {
  section: RequirementSection
  epics: RequirementNode[]
}

function groupBySection(tree: RequirementNode[], byId: Map<string, Requirement>): SectionGroup[] {
  const buckets = new Map<RequirementSection, RequirementNode[]>()
  for (const s of REQUIREMENT_DOC_SECTIONS) buckets.set(s, [])
  for (const epic of tree) {
    const section = getRequirementSection(epic, byId)
    buckets.get(section)!.push(epic)
  }
  return REQUIREMENT_DOC_SECTIONS
    .map(s => ({ section: s, epics: buckets.get(s)! }))
    .filter(g => g.epics.length > 0)
}

function flatIds(tree: RequirementNode[]): string[] {
  const out: string[] = []
  function visit(nodes: RequirementNode[]) {
    for (const n of nodes) {
      out.push(n.id)
      if (n.children.length) visit(n.children)
    }
  }
  visit(tree)
  return out
}

export function OutlineTOC({ tree, requirements, pinned, onTogglePin, onClose }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null)

  const byId = useMemo(() => new Map(requirements.map(r => [r.id, r])), [requirements])
  const grouped = useMemo(() => groupBySection(tree, byId), [tree, byId])
  const ids = useMemo(() => flatIds(tree), [tree])

  useEffect(() => {
    if (ids.length === 0) return
    const targets = ids
      .map(id => document.querySelector<HTMLElement>(`[data-outline-id="${id}"]`))
      .filter((el): el is HTMLElement => Boolean(el))
    if (!targets.length) return
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) {
          const id = visible[0].target.getAttribute('data-outline-id')
          if (id) setActiveId(id)
        }
      },
      { rootMargin: '-10% 0% -65% 0%', threshold: 0 },
    )
    targets.forEach(t => observer.observe(t))
    return () => observer.disconnect()
  }, [ids.join(',')])

  function jumpTo(id: string) {
    const el = document.querySelector<HTMLElement>(`[data-outline-id="${id}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setActiveId(id)
    requestAnimationFrame(() => {
      el.querySelector<HTMLElement>('.outline-title')?.focus()
    })
  }

  return (
    <aside
      aria-label="Outline table of contents"
      style={{
        width: 220,
        flexShrink: 0,
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 10px 8px 14px',
          flexShrink: 0,
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--color-fg-muted)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          On this page
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TocIconBtn onClick={onTogglePin} title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}>
            {pinned ? <Pin size={12} /> : <PinOff size={12} />}
          </TocIconBtn>
          <TocIconBtn onClick={onClose} title="Close">
            <X size={12} />
          </TocIconBtn>
        </div>
      </div>

      {/* Entries */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 24px' }}>
        {ids.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-fg-muted)', padding: '4px 6px' }}>
            Nothing here yet.
          </div>
        )}
        {grouped.map((g, i) => (
          <div key={g.section} style={{ marginTop: i === 0 ? 0 : 16 }}>
            <SectionHeading
              label={REQUIREMENT_DOC_SECTION_LABELS[g.section]}
              count={g.epics.length}
            />
            {g.epics.map(epic => (
              <div key={epic.id}>
                <EpicBtn
                  name={epic.name || 'Untitled'}
                  draft={!epic.name}
                  active={activeId === epic.id}
                  onClick={() => jumpTo(epic.id)}
                />
                {epic.children.map(story => (
                  <StoryBtn
                    key={story.id}
                    name={story.name || 'Untitled'}
                    draft={!story.name}
                    active={activeId === story.id}
                    onClick={() => jumpTo(story.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </aside>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function TocIconBtn({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        width: 22, height: 22,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        color: 'var(--color-fg-muted)',
        transition: 'background 100ms, color 100ms',
        padding: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-warm-200)'; e.currentTarget.style.color = 'var(--color-fg)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--color-fg-muted)' }}
    >
      {children}
    </button>
  )
}

function SectionHeading({ label, count }: { label: string; count: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        padding: '0 6px',
        marginBottom: 3,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-fg)', letterSpacing: '0.01em', fontFamily: 'var(--font-display)' }}>
        {label}
      </span>
      <span style={{ fontSize: 10, color: 'var(--color-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
        {count}
      </span>
    </div>
  )
}

function EpicBtn({ name, draft, active, onClick }: { name: string; draft: boolean; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={name}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 0,
        cursor: 'pointer',
        borderRadius: 5,
        padding: '3px 6px',
        marginTop: 6,
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize: 12.5,
        letterSpacing: '-0.005em',
        background: active ? 'var(--color-warm-100)' : 'transparent',
        color: draft ? 'var(--color-fg-muted)' : 'var(--color-fg)',
        fontStyle: draft ? 'italic' : 'normal',
        transition: 'background 100ms',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--color-warm-100)' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
    >
      {name}
    </button>
  )
}

function StoryBtn({ name, draft, active, onClick }: { name: string; draft: boolean; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={name}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 0,
        cursor: 'pointer',
        borderRadius: 5,
        padding: '2.5px 6px 2.5px 16px',
        fontWeight: 500,
        fontSize: 12,
        background: active ? 'var(--color-warm-100)' : 'transparent',
        color: draft ? 'var(--color-fg-muted)' : (active ? 'var(--color-fg)' : 'var(--color-fg-secondary)'),
        fontStyle: draft ? 'italic' : 'normal',
        transition: 'background 100ms',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--color-warm-100)' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
    >
      {name}
    </button>
  )
}
